import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, TextField } from '@heroui/react';
import { Button } from '@/components/Button';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { useVaultShelves } from '@/lib/useVaultShelves';
import { CheckIcon, FolderIcon, PlusIcon } from '@/components/icons';
import { LIMITS } from '@bunker/shared/limits';

/**
 * "На полку" — pick which shelves a saved vault message lives on. Mirrors the
 * mobile AddToShelfSheet: seed the checkboxes from the message's current
 * membership, let the user toggle / create inline, then write the full set in
 * one PUT. Shelves are tags (a message can be on several), capped server-side.
 */
export function ShelfPickerModal(props: { messageId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { shelves, loading, create } = useVaultShelves();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Seed the current membership for this message. If this fails we must NOT
  // let the user save: an empty selection would silently wipe every shelf the
  // message is already on. So we surface the error and block save until retry.
  const seedMembership = useCallback(() => {
    let cancelled = false;
    setLoadFailed(false);
    api
      .getMessageShelves(props.messageId)
      .then((res) => {
        if (!cancelled) setSelected(new Set(res.shelfIds));
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadFailed(true);
          toast.error(...errToToast(err, t('shelves.loadError')));
        }
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [props.messageId, t]);

  useEffect(() => seedMembership(), [seedMembership]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function createInline() {
    const name = draft.trim();
    if (!name || creating) return;
    setCreating(true);
    const shelf = await create(name);
    setCreating(false);
    if (shelf) {
      setSelected((prev) => new Set(prev).add(shelf.id));
      setDraft('');
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      toast.error(t('shelves.createError'));
    }
  }

  async function save() {
    if (saving || loadFailed) return;
    setSaving(true);
    try {
      await api.setMessageShelves(props.messageId, { shelfIds: [...selected] });
      toast.success(t('shelves.saved'));
      props.onClose();
    } catch {
      toast.error(t('shelves.saveError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal.Backdrop isOpen onOpenChange={(v) => { if (!v) props.onClose(); }}>
      <Modal.Container size="sm" scroll="inside">
        <Modal.Dialog>
          <Modal.CloseTrigger />
          <Modal.Header>
            <Modal.Heading>
              <span className="flex items-center gap-2">
                <FolderIcon className="h-5 w-5 text-muted" />
                {t('shelves.addTitle')}
              </span>
            </Modal.Heading>
          </Modal.Header>
          <Modal.Body>
            <div className="flex flex-col gap-3">
              {/* Inline create */}
              <div className="flex items-end gap-2">
                <TextField fullWidth value={draft} onChange={setDraft}>
                  <Input
                    ref={inputRef}
                    variant="secondary"
                    placeholder={t('shelves.namePlaceholder')}
                    maxLength={LIMITS.vaultShelf.nameMax}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void createInline();
                      }
                    }}
                  />
                </TextField>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={createInline}
                  disabled={!draft.trim()}
                  loading={creating}
                  aria-label={t('shelves.create')}
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </div>

              {/* Membership load failed — block save so we don't wipe shelves. */}
              {loadFailed && (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-ink">
                  <span className="min-w-0 flex-1 text-ink-muted">{t('shelves.loadErrorHint')}</span>
                  <Button variant="ghost" size="sm" onClick={seedMembership}>
                    {t('shelves.retry')}
                  </Button>
                </div>
              )}

              {/* Existing shelves */}
              <div className="scrollbar-thin -mx-1 max-h-[44vh] overflow-y-auto px-1">
                {loaded && !loading && shelves.length === 0 ? (
                  <p className="px-1 py-6 text-center text-sm text-ink-muted">
                    {t('shelves.emptyPicker')}
                  </p>
                ) : (
                  shelves.map((shelf) => {
                    const on = selected.has(shelf.id);
                    return (
                      <button
                        key={shelf.id}
                        type="button"
                        onClick={() => toggle(shelf.id)}
                        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/8"
                      >
                        <span
                          className={
                            'grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-colors ' +
                            (on ? 'border-primary bg-primary text-white' : 'border-separator text-transparent')
                          }
                        >
                          <CheckIcon className="h-3.5 w-3.5" />
                        </span>
                        <FolderIcon className="h-4 w-4 shrink-0 text-ink-muted" />
                        <span className="flex-1 truncate text-sm text-ink">{shelf.name}</span>
                        {shelf.count > 0 && (
                          <span className="shrink-0 text-xs tabular-nums text-ink-muted">{shelf.count}</span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button variant="ghost" size="sm" onClick={props.onClose}>
                  {t('common.cancel')}
                </Button>
                <Button variant="primary" size="sm" onClick={save} loading={saving} disabled={loadFailed}>
                  {t('common.done')}
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
